import { storableError } from '../../util/errors';
import { fetchAllExperts } from '../../util/api';
import { fetchPageAssets } from '../../ducks/hostedAssets.duck';

// How many experts to fetch per request for the roster. The page groups and
// renders the full roster client-side, so we page through ALL results (the
// endpoint is Algolia-backed and cached server-side, and caps perPage at 200).
export const ROSTER_PER_PAGE = 200;

// Safety cap on how many pages the roster will fetch, so an unexpectedly large
// result set can't fire an unbounded number of requests. 25 * 200 = 5000.
export const MAX_ROSTER_PAGES = 25;

// The Console-hosted page asset that defines the roster's discipline sections
// (titles + which category keys feed each). Managed under
// Content > Pages > our-experts in the Sharetribe Console.
export const ASSET_NAME = 'our-experts';

// ================ Action types ================ //

export const LOAD_EXPERTS_REQUEST = 'app/ExpertsPage/LOAD_EXPERTS_REQUEST';
export const LOAD_EXPERTS_SUCCESS = 'app/ExpertsPage/LOAD_EXPERTS_SUCCESS';
export const LOAD_EXPERTS_ERROR = 'app/ExpertsPage/LOAD_EXPERTS_ERROR';

// ================ Reducer ================ //

const initialState = {
  experts: [],
  expertsMeta: null,
  expertsInProgress: false,
  expertsError: null,
  pagination: null,
};

const expertsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case LOAD_EXPERTS_REQUEST:
      return {
        ...state,
        expertsInProgress: true,
        expertsError: null,
      };

    case LOAD_EXPERTS_SUCCESS:
      return {
        ...state,
        experts: payload.experts,
        expertsMeta: payload.meta,
        expertsInProgress: false,
        expertsError: null,
        pagination: payload.pagination,
      };

    case LOAD_EXPERTS_ERROR:
      return {
        ...state,
        expertsInProgress: false,
        expertsError: payload,
        experts: [],
        expertsMeta: null,
        pagination: null,
      };

    default:
      return state;
  }
};

export default expertsPageReducer;

// ================ Action creators ================ //

export const loadExpertsRequest = () => ({ type: LOAD_EXPERTS_REQUEST });

export const loadExpertsSuccess = (experts, meta, pagination) => ({
  type: LOAD_EXPERTS_SUCCESS,
  payload: { experts, meta, pagination },
});

export const loadExpertsError = error => ({
  type: LOAD_EXPERTS_ERROR,
  payload: error,
  error: true,
});

// ================ Thunks ================ //

export const loadExperts = params => async dispatch => {
  dispatch(loadExpertsRequest());

  const { perPage = ROSTER_PER_PAGE } = params || {};

  try {
    // Fetch the first page to learn how many pages the roster spans.
    const firstResponse = await fetchAllExperts({ page: 1, perPage });
    const { users: firstUsers, meta } = firstResponse;
    const totalPages = Math.min(meta?.totalPages || 1, MAX_ROSTER_PAGES);

    let experts = Array.isArray(firstUsers) ? [...firstUsers] : [];

    // Fetch any remaining pages in parallel and append, so the roster shows the
    // full set of experts/masters even beyond a single page's worth.
    if (totalPages > 1) {
      const remaining = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          fetchAllExperts({ page: i + 2, perPage }).then(r => r.users || [])
        )
      );
      experts = experts.concat(...remaining);
    }

    if ((meta?.totalPages || 1) > MAX_ROSTER_PAGES) {
      console.warn(
        `[ExpertsPage] roster truncated at ${MAX_ROSTER_PAGES} pages ` +
          `(${meta.totalItems} experts across ${meta.totalPages} pages)`
      );
    }

    const pagination = {
      page: 1,
      perPage: meta?.perPage,
      totalItems: meta?.totalItems,
      totalPages: meta?.totalPages,
      fetchedItems: experts.length,
    };

    dispatch(loadExpertsSuccess(experts, meta, pagination));
    return { users: experts, meta };
  } catch (e) {
    const error = storableError(e);
    dispatch(loadExpertsError(error));
    throw error;
  }
};

// ================ Page load data ================ //

// Route-level loader (SSR + client-side transitions). Loads two independent
// things in parallel:
//   1. the roster experts (Algolia-backed), and
//   2. the `our-experts` page asset, which defines the discipline sections
//      (titles + category grouping) editable in the Console.
// Each is fault-isolated: a failed experts fetch surfaces an inline retry in
// the component; a missing/failed asset falls back to the built-in default
// grouping. Neither should break SSR, so errors are swallowed here.
export const loadData = () => dispatch => {
  const pageAsset = { ourExperts: `content/pages/${ASSET_NAME}.json` };
  return Promise.all([
    dispatch(loadExperts({ page: 1, perPage: ROSTER_PER_PAGE })).catch(() => null),
    dispatch(fetchPageAssets(pageAsset, true)).catch(() => null),
  ]);
};
